import React, { useRef, useEffect, useCallback, useState } from 'react';
import { Stage, Layer, Rect, Text as KonvaText, Image as KonvaImage, Circle, Line, Transformer, Group } from 'react-konva';
import Konva from 'konva';
import useImage from 'use-image';
import { getProxiedImageUrl, REALPROP_GOLD } from './constants';
import apiClient from '../../utils/api';

// Snap rotation to 0/45/90… within tolerance (Part A)
const ROTATION_SNAPS = [0, 45, 90, 135, 180, 225, 270, 315];

// Touch-friendly transformer config: larger handles when on mobile/touch devices
const IS_TOUCH = typeof window !== 'undefined' && (
  ('ontouchstart' in window) || (navigator.maxTouchPoints > 0)
);
const TR_ANCHOR_SIZE = IS_TOUCH ? 16 : 8;
const TR_STROKE = IS_TOUCH ? 2 : 1;

// --- URLImage: Standalone image with transform + crop ---
const URLImage = ({ image, isSelected, onSelect, onChange }) => {
  const proxiedSrc = getProxiedImageUrl(image.src);
  const [img] = useImage(proxiedSrc, 'anonymous');
  const shapeRef = useRef();
  const trRef = useRef();

  useEffect(() => {
    if (isSelected && trRef.current && shapeRef.current) {
      trRef.current.nodes([shapeRef.current]);
      trRef.current.getLayer().batchDraw();
    }
  }, [isSelected]);

  const getCropConfig = () => {
    if (!img || !image.wasPhotoPlaceholder) return null;
    const iR = img.width / img.height;
    const tR = image.width / image.height;
    const z = Math.max(0.5, Math.min(3, image.imageZoom || 1));
    const oX = image.imageOffsetX ?? 0.5;
    const oY = image.imageOffsetY ?? 0.5;
    let cW, cH;
    if (iR > tR) { cH = img.height / z; cW = cH * tR; }
    else { cW = img.width / z; cH = cW / tR; }
    const maxX = img.width - cW;
    const maxY = img.height - cH;
    return {
      x: Math.max(0, Math.min(maxX, oX * maxX)),
      y: Math.max(0, Math.min(maxY, oY * maxY)),
      width: cW, height: cH
    };
  };

  return (
    <>
      <KonvaImage
        ref={shapeRef} image={img}
        x={image.x} y={image.y} width={image.width} height={image.height}
        rotation={image.rotation || 0}
        draggable={image.draggable !== false} crop={getCropConfig()}
        onClick={onSelect} onTap={onSelect}
        onDragEnd={(e) => onChange({ ...image, x: e.target.x(), y: e.target.y() })}
        onTransformEnd={(e) => {
          const n = shapeRef.current;
          const sx = n.scaleX(), sy = n.scaleY();
          n.scaleX(1); n.scaleY(1);
          onChange({ ...image, x: n.x(), y: n.y(), width: Math.max(5, n.width() * sx), height: Math.max(5, n.height() * sy), rotation: n.rotation() });
        }}
      />
      {isSelected && (
        <Transformer anchorSize={TR_ANCHOR_SIZE} borderStrokeWidth={TR_STROKE} anchorStrokeWidth={TR_STROKE} ref={trRef} rotationSnaps={ROTATION_SNAPS} rotationSnapTolerance={6}
          keepRatio
          enabledAnchors={['top-left', 'top-right', 'bottom-left', 'bottom-right']}
          boundBoxFunc={(o, n) => (n.width < 5 || n.height < 5 ? o : n)} />
      )}
    </>
  );
};

// --- DraggableShape (rect / circle) ---
const DraggableShape = ({ element, isSelected, onSelect, onChange }) => {
  const shapeRef = useRef();
  const trRef = useRef();

  useEffect(() => {
    if (isSelected && trRef.current && shapeRef.current) {
      trRef.current.nodes([shapeRef.current]);
      trRef.current.getLayer().batchDraw();
    }
  }, [isSelected]);

  const ShapeComp = element.type === 'circle' ? Circle : Rect;
  const props = { ...element };
  // Remove non-Konva props
  delete props.editable;
  delete props.visible;

  return (
    <>
      <ShapeComp
        ref={shapeRef}
        {...props}
        onClick={onSelect}
        onTap={onSelect}
        onDragEnd={(e) => onChange({ ...element, x: e.target.x(), y: e.target.y() })}
        onTransformEnd={(e) => {
          const n = shapeRef.current;
          const sx = n.scaleX(), sy = n.scaleY();
          n.scaleX(1); n.scaleY(1);
          if (element.type === 'circle') {
            onChange({ ...element, x: n.x(), y: n.y(), radius: Math.max(5, (element.radius || 60) * Math.max(sx, sy)), rotation: n.rotation() });
          } else {
            onChange({ ...element, x: n.x(), y: n.y(), width: Math.max(5, n.width() * sx), height: Math.max(5, n.height() * sy), rotation: n.rotation() });
          }
        }}
      />
      {isSelected && (
        <Transformer anchorSize={TR_ANCHOR_SIZE} borderStrokeWidth={TR_STROKE} anchorStrokeWidth={TR_STROKE} ref={trRef} rotationSnaps={ROTATION_SNAPS} rotationSnapTolerance={6} boundBoxFunc={(o, n) => (n.width < 5 || n.height < 5 ? o : n)} />
      )}
    </>
  );
};

// --- EditableText ---
// Supports `autoFit`: when text would wrap to multiple lines, automatically
// shrink fontSize so it stays on a single line within the element's width.
// Used for {{address}}, {{price}} etc. so long values from the active listing
// don't break the layout. The original fontSize is preserved as `fontSizeBase`
// so resizing the box manually still respects the designer's intent.
const computeAutoFitFontSize = (text, baseSize, width, fontFamily, fontStyle) => {
  if (!text || !width) return baseSize;
  if (typeof window === 'undefined' || typeof document === 'undefined') return baseSize;
  try {
    if (!computeAutoFitFontSize._ctx) {
      const c = document.createElement('canvas');
      computeAutoFitFontSize._ctx = c.getContext('2d');
    }
    const ctx = computeAutoFitFontSize._ctx;
    const weight = (fontStyle === 'bold' || fontStyle === 'italic bold' || fontStyle === 'bold italic') ? 'bold' : 'normal';
    const italic = (fontStyle === 'italic' || fontStyle === 'italic bold' || fontStyle === 'bold italic') ? 'italic ' : '';
    ctx.font = `${italic}${weight} ${baseSize}px ${fontFamily || 'Arial'}`;
    const w = ctx.measureText(text).width;
    if (w <= width) return baseSize;
    // Shrink proportionally, but never go below 8px
    return Math.max(8, Math.floor(baseSize * (width / w)));
  } catch (_) {
    return baseSize;
  }
};

const EditableText = ({ element, isSelected, onSelect, onChange, onDoubleEdit }) => {
  const textRef = useRef();
  const trRef = useRef();

  useEffect(() => {
    if (isSelected && trRef.current && textRef.current) {
      trRef.current.nodes([textRef.current]);
      trRef.current.getLayer().batchDraw();
    }
  }, [isSelected]);

  // Apply auto-fit: pick the largest fontSize that fits the text on one line
  const baseSize = element.fontSizeBase || element.fontSize || 24;
  const effectiveFontSize = element.autoFit
    ? computeAutoFitFontSize(element.text || '', baseSize, element.width, element.fontFamily, element.fontStyle)
    : (element.fontSize || baseSize);
  const effectiveWrap = element.autoFit ? 'none' : (element.wrap || 'word');

  return (
    <>
      <KonvaText
        ref={textRef} {...element}
        fontSize={effectiveFontSize}
        wrap={effectiveWrap}
        onClick={onSelect} onTap={onSelect}
        onDblClick={onDoubleEdit} onDblTap={onDoubleEdit}
        onDragEnd={(e) => onChange({ ...element, x: e.target.x(), y: e.target.y() })}
        onTransformEnd={(e) => {
          const n = textRef.current;
          const anchor = trRef.current?.getActiveAnchor() || '';
          const isCorner = anchor.includes('top') || anchor.includes('bottom');
          const sx = n.scaleX();
          n.scaleX(1); n.scaleY(1);
          if (element.autoFit) {
            // Auto-fit text: resize only changes width — fontSize is computed.
            onChange({ ...element, x: n.x(), y: n.y(), width: Math.max(30, n.width() * sx), rotation: n.rotation() });
          } else if (isCorner) {
            // Corner handles scale the font size proportionally (Canva behaviour).
            onChange({ ...element, x: n.x(), y: n.y(), fontSize: Math.max(12, baseSize * sx), rotation: n.rotation() });
          } else {
            // Side handles change the text box width; text re-wraps, font unchanged.
            onChange({ ...element, x: n.x(), y: n.y(), width: Math.max(30, n.width() * sx), rotation: n.rotation() });
          }
        }}
      />
      {isSelected && (
        <Transformer anchorSize={TR_ANCHOR_SIZE} borderStrokeWidth={TR_STROKE} anchorStrokeWidth={TR_STROKE} ref={trRef} rotationSnaps={ROTATION_SNAPS} rotationSnapTolerance={6} keepRatio
          enabledAnchors={['middle-left', 'middle-right', 'top-left', 'top-right', 'bottom-left', 'bottom-right']}
          boundBoxFunc={(o, n) => { n.width = Math.max(30, n.width); return n; }} />
      )}
    </>
  );
};

// --- QRCodeElement ---
const QRCodeElement = ({ element, isSelected, onSelect, onChange }) => {
  const [qrImage, setQrImage] = useState(null);
  const [loadedImage] = useImage(qrImage, 'anonymous');
  const shapeRef = useRef();
  const trRef = useRef();

  useEffect(() => {
    if (isSelected && trRef.current && shapeRef.current) {
      trRef.current.nodes([shapeRef.current]);
      trRef.current.getLayer().batchDraw();
    }
  }, [isSelected]);

  useEffect(() => {
    if (element.qrUrl) {
      apiClient.post('/ai/generate-qr', {
        url: element.qrUrl,
        size: Math.max(element.width, element.height) || 150
      }).then(r => { if (r.data.success) setQrImage(r.data.qr_code); })
        .catch(() => {});
    }
  }, [element.qrUrl, element.width, element.height]);

  const w = element.width || 150;
  const h = element.height || 150;

  if (loadedImage && element.qrUrl) {
    return (
      <>
        <KonvaImage
          ref={shapeRef} image={loadedImage}
          x={element.x} y={element.y} width={w} height={h}
          rotation={element.rotation || 0}
          draggable={element.draggable !== false}
          onClick={onSelect} onTap={onSelect}
          onDragEnd={(e) => onChange({ ...element, x: e.target.x(), y: e.target.y() })}
          onTransformEnd={(e) => {
            const n = shapeRef.current;
            const sx = n.scaleX(), sy = n.scaleY();
            n.scaleX(1); n.scaleY(1);
            onChange({ ...element, x: n.x(), y: n.y(), width: Math.max(50, n.width() * sx), height: Math.max(50, n.height() * sy), rotation: n.rotation() });
          }}
        />
        {isSelected && (
          <Transformer anchorSize={TR_ANCHOR_SIZE} borderStrokeWidth={TR_STROKE} anchorStrokeWidth={TR_STROKE} ref={trRef} rotationSnaps={ROTATION_SNAPS} rotationSnapTolerance={6} keepRatio boundBoxFunc={(o, n) => (n.width < 50 || n.height < 50 ? o : n)} />
        )}
      </>
    );
  }

  return (
    <Group x={element.x} y={element.y} draggable={element.draggable !== false} onClick={onSelect} onTap={onSelect}
      onDragEnd={(e) => onChange({ ...element, x: e.target.x(), y: e.target.y() })}>
      <Rect width={w} height={h} fill={element.backgroundColor || '#F5F5F5'}
        stroke={isSelected ? '#0ea5e9' : '#CCCCCC'} strokeWidth={isSelected ? 3 : 2} dash={[5, 5]} cornerRadius={4} />
      <KonvaText x={0} y={h / 2 - 20} width={w} text="QR CODE" fontSize={14} fill="#888888" fontFamily="Arial" fontStyle="bold" align="center" />
      <KonvaText x={0} y={h / 2} width={w} text="(Set listing URL)" fontSize={10} fill="#AAAAAA" fontFamily="Arial" align="center" />
    </Group>
  );
};

// --- PhotoGridSlot: Clip-based with interactive pan & zoom ---
const PhotoGridSlot = ({ slot, slotIndex, isSelected, isDragOver, onSlotClick, gridId, onPanChange }) => {
  const proxiedSrc = getProxiedImageUrl(slot.src);
  const [img] = useImage(proxiedSrc, 'anonymous');

  const zoom = slot.imageZoom || 1;
  const offsetX = slot.imageOffsetX ?? 0.5;
  const offsetY = slot.imageOffsetY ?? 0.5;

  // Cover-fit dimensions scaled by zoom
  let imgW = slot.width, imgH = slot.height;
  if (img) {
    const iR = img.width / img.height;
    const sR = slot.width / slot.height;
    if (iR > sR) { imgH = slot.height; imgW = slot.height * iR; }
    else { imgW = slot.width; imgH = slot.width / iR; }
    imgW *= zoom;
    imgH *= zoom;
  }

  // Max travel distance & position from normalized offsets
  const maxTX = Math.max(0, imgW - slot.width);
  const maxTY = Math.max(0, imgH - slot.height);
  const posX = slot.x - offsetX * maxTX;
  const posY = slot.y - offsetY * maxTY;

  const handleDragMove = useCallback((e) => {
    e.cancelBubble = true;
    const n = e.target;
    n.x(Math.min(slot.x, Math.max(slot.x - maxTX, n.x())));
    n.y(Math.min(slot.y, Math.max(slot.y - maxTY, n.y())));
  }, [slot.x, slot.y, maxTX, maxTY]);

  const handleDragEnd = useCallback((e) => {
    e.cancelBubble = true;
    const n = e.target;
    const nX = maxTX > 0 ? (slot.x - n.x()) / maxTX : 0.5;
    const nY = maxTY > 0 ? (slot.y - n.y()) / maxTY : 0.5;
    onPanChange(gridId, slotIndex, Math.max(0, Math.min(1, nX)), Math.max(0, Math.min(1, nY)));
  }, [maxTX, maxTY, slot.x, slot.y, gridId, slotIndex, onPanChange]);

  return (
    <Group>
      {/* Slot border/background - named for hit detection */}
      <Rect
        name="grid-slot-bg"
        slotGridId={gridId}
        slotIdx={slotIndex}
        x={slot.x} y={slot.y} width={slot.width} height={slot.height}
        fill={isDragOver ? 'rgba(212,175,55,0.15)' : (!slot.src ? '#F3F4F6' : 'transparent')}
        stroke={isDragOver ? REALPROP_GOLD : (isSelected ? '#8B5CF6' : (slot.src ? 'transparent' : '#D1D5DB'))}
        strokeWidth={isDragOver ? 3 : (isSelected ? 3 : 1)}
        cornerRadius={4}
        listening={true}
        onClick={(e) => { e.cancelBubble = true; onSlotClick(); }}
        onTap={(e) => { e.cancelBubble = true; onSlotClick(); }}
      />

      {/* Clipped image container - the "window" for the photo */}
      {slot.src && img && (
        <Group clipX={slot.x} clipY={slot.y} clipWidth={slot.width} clipHeight={slot.height}>
          <KonvaImage
            x={posX} y={posY} width={imgW} height={imgH}
            image={img}
            draggable={isSelected}
            onDragStart={(e) => { e.cancelBubble = true; }}
            onDragMove={handleDragMove}
            onDragEnd={handleDragEnd}
            onClick={(e) => { e.cancelBubble = true; onSlotClick(); }}
            onTap={(e) => { e.cancelBubble = true; onSlotClick(); }}
          />
        </Group>
      )}

      {/* Empty slot placeholder */}
      {!slot.src && (
        <Group onClick={(e) => { e.cancelBubble = true; onSlotClick(); }}
          onTap={(e) => { e.cancelBubble = true; onSlotClick(); }}>
          <Rect x={slot.x + slot.width / 2 - 20} y={slot.y + slot.height / 2 - 20}
            width={40} height={40} fill="#E5E7EB" cornerRadius={20} />
          <KonvaText x={slot.x + slot.width / 2 - 6} y={slot.y + slot.height / 2 - 10}
            text="+" fontSize={24} fill="#9CA3AF" fontStyle="bold" />
          <KonvaText x={slot.x + slot.width / 2 - 25} y={slot.y + slot.height / 2 + 25}
            text="Add Photo" fontSize={11} fill="#9CA3AF" />
        </Group>
      )}

      {/* Pan indicator badge when selected */}
      {isSelected && slot.src && (
        <Group>
          <Rect x={slot.x + slot.width - 32} y={slot.y + 4} width={28} height={20}
            fill="rgba(139,92,246,0.9)" cornerRadius={4} />
          <KonvaText x={slot.x + slot.width - 28} y={slot.y + 7}
            text="Pan" fontSize={10} fill="white" fontFamily="Arial" />
        </Group>
      )}
    </Group>
  );
};

// --- ResizablePhotoGrid: Container group with drag + resize ---
const ResizablePhotoGrid = ({ element, isSelected, onSelect, onChange, onSlotSelect, selectedSlotIndex, dragOverSlotIndex, onPanChange }) => {
  const groupRef = useRef();
  const trRef = useRef();

  useEffect(() => {
    if (isSelected && trRef.current && groupRef.current) {
      trRef.current.nodes([groupRef.current]);
      trRef.current.getLayer().batchDraw();
    }
  }, [isSelected]);

  const handleDragEnd = (e) => {
    const node = e.target;
    const dx = node.x() - element.x;
    const dy = node.y() - element.y;
    const updatedSlots = element.slots.map(s => ({ ...s, x: s.x + dx, y: s.y + dy }));
    onChange({ ...element, x: node.x(), y: node.y(), slots: updatedSlots });
  };

  const handleTransformEnd = () => {
    const node = groupRef.current;
    const sx = node.scaleX(), sy = node.scaleY();
    node.scaleX(1); node.scaleY(1);
    const nW = Math.max(100, element.width * sx);
    const nH = Math.max(100, element.height * sy);
    const wR = nW / element.width, hR = nH / element.height;
    const updatedSlots = element.slots.map(s => ({
      ...s,
      x: element.x + (s.x - element.x) * wR,
      y: element.y + (s.y - element.y) * hR,
      width: s.width * wR,
      height: s.height * hR
    }));
    onChange({ ...element, x: node.x(), y: node.y(), width: nW, height: nH, slots: updatedSlots, rotation: node.rotation() });
  };

  return (
    <>
      <Group ref={groupRef} x={element.x} y={element.y} rotation={element.rotation || 0}
        draggable onClick={() => onSelect(element.id)} onTap={() => onSelect(element.id)}
        onDragEnd={handleDragEnd} onTransformEnd={handleTransformEnd}>

        <Rect x={0} y={0} width={element.width} height={element.height}
          fill="transparent" stroke={isSelected ? '#8B5CF6' : 'transparent'} strokeWidth={2} dash={[5, 5]} />

        {element.slots.map((slot, i) => {
          const relSlot = { ...slot, x: slot.x - element.x, y: slot.y - element.y };
          return (
            <PhotoGridSlot
              key={i} slot={relSlot} slotIndex={i} gridId={element.id}
              isSelected={selectedSlotIndex === i && isSelected}
              isDragOver={dragOverSlotIndex === i}
              onSlotClick={() => onSlotSelect(element.id, i)}
              onPanChange={onPanChange}
            />
          );
        })}
      </Group>
      {isSelected && (
        <Transformer anchorSize={TR_ANCHOR_SIZE} borderStrokeWidth={TR_STROKE} anchorStrokeWidth={TR_STROKE} ref={trRef} rotationSnaps={ROTATION_SNAPS} rotationSnapTolerance={6} flipEnabled={false}
          boundBoxFunc={(o, n) => (Math.abs(n.width) < 100 || Math.abs(n.height) < 100 ? o : n)} />
      )}
    </>
  );
};

// --- Main CanvasStage ---
const MIN_SCALE = 0.2;
const MAX_SCALE = 2.0;

// Distance between two touch points.
const touchDistance = (a, b) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
// Midpoint between two touch points.
const touchMidpoint = (a, b) => ({ x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 });

export const CanvasStage = ({ engine }) => {
  const {
    stageRef, elements, selectedId, selectedGridSlot, selectedPhotoPlaceholder,
    dragOverSlot, canvasWidth, canvasHeight, scale, bgColor, brandKit,
    handleSelect, handleGridSlotSelect, handlePhotoPlaceholderSelect, handleDeselect,
    updateElement, handleGridPanChange,
    stagePos, setStagePos, setScale,
  } = engine;

  // Ref-backed gesture state — avoids re-renders during the active pinch.
  // velocity (vx,vy) is in screen px / frame and is updated on every move.
  // momentumRaf is the active rAF id for the inertial decay loop.
  const gestureRef = React.useRef({
    active: false, lastDist: 0, lastMid: null,
    scaleAtStart: 1, posAtStart: { x: 0, y: 0 },
    lastTap: 0,
    vx: 0, vy: 0, lastMoveTime: 0,
    momentumRaf: null,
  });

  // Friction per frame at 60fps. 0.92 ≈ ~85% velocity left after 100ms → feels alive but settles fast.
  const MOMENTUM_FRICTION = 0.92;
  // Below this px/frame threshold we kill the loop entirely so we don't burn CPU.
  const MOMENTUM_MIN_VELOCITY = 0.15;

  const stopMomentum = React.useCallback(() => {
    if (gestureRef.current.momentumRaf != null) {
      cancelAnimationFrame(gestureRef.current.momentumRaf);
      gestureRef.current.momentumRaf = null;
    }
  }, []);

  const startMomentum = React.useCallback(() => {
    const tick = () => {
      const g = gestureRef.current;
      // Apply velocity to position, then decay it.
      if (Math.hypot(g.vx, g.vy) < MOMENTUM_MIN_VELOCITY) {
        g.momentumRaf = null;
        g.vx = 0; g.vy = 0;
        return;
      }
      setStagePos(prev => ({ x: prev.x + g.vx, y: prev.y + g.vy }));
      g.vx *= MOMENTUM_FRICTION;
      g.vy *= MOMENTUM_FRICTION;
      g.momentumRaf = requestAnimationFrame(tick);
    };
    gestureRef.current.momentumRaf = requestAnimationFrame(tick);
  }, [setStagePos]);

  // Clean up any in-flight momentum loop on unmount.
  useEffect(() => () => stopMomentum(), [stopMomentum]);

  const handleTouchStart = React.useCallback((e) => {
    // A new touch always kills any in-flight inertia.
    stopMomentum();
    const touches = e.evt?.touches;
    if (!touches || touches.length < 2) {
      // single-tap: detect double-tap-to-reset
      const now = Date.now();
      if (now - gestureRef.current.lastTap < 320) {
        // Double-tap → reset view
        setStagePos({ x: 0, y: 0 });
      }
      gestureRef.current.lastTap = now;
      return;
    }
    // 2-finger gesture begins → freeze base values, stop element drags from propagating
    e.evt.preventDefault();
    gestureRef.current = {
      ...gestureRef.current,
      active: true,
      lastDist: touchDistance(touches[0], touches[1]),
      lastMid: touchMidpoint(touches[0], touches[1]),
      scaleAtStart: scale,
      posAtStart: { ...stagePos },
      lastTap: 0,
      vx: 0, vy: 0, lastMoveTime: performance.now(),
    };
  }, [scale, stagePos, setStagePos, stopMomentum]);

  const handleTouchMove = React.useCallback((e) => {
    const touches = e.evt?.touches;
    if (!gestureRef.current.active || !touches || touches.length < 2) return;
    e.evt.preventDefault();

    const dist = touchDistance(touches[0], touches[1]);
    const mid  = touchMidpoint(touches[0], touches[1]);
    const g = gestureRef.current;

    // Pinch — relative to gesture start to keep it smooth.
    const ratio = dist / Math.max(1, g.lastDist);
    const nextScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, g.scaleAtStart * ratio));

    // Anchor zoom around pinch midpoint so the spot between your fingers
    // stays under your fingers as you pinch (the natural-feeling behaviour).
    // Stage transform: screen = world * scale + pos  →  world = (screen - pos) / scale
    const stageEl = stageRef.current?.container();
    const rect = stageEl ? stageEl.getBoundingClientRect() : { left: 0, top: 0 };
    const localMid = { x: g.lastMid.x - rect.left, y: g.lastMid.y - rect.top };
    const worldX = (localMid.x - g.posAtStart.x) / g.scaleAtStart;
    const worldY = (localMid.y - g.posAtStart.y) / g.scaleAtStart;

    // Add midpoint translation (two-finger drag).
    const dx = mid.x - g.lastMid.x;
    const dy = mid.y - g.lastMid.y;

    // Track velocity for momentum — px / frame, smoothed with the previous value
    // so brief jitter on lift doesn't fling the canvas the wrong way.
    const now = performance.now();
    const dt = Math.max(1, now - g.lastMoveTime);
    const frame = 16.67; // 60fps target frame time
    const instVx = (dx / dt) * frame;
    const instVy = (dy / dt) * frame;
    g.vx = g.vx * 0.4 + instVx * 0.6;
    g.vy = g.vy * 0.4 + instVy * 0.6;
    g.lastMoveTime = now;

    setScale(nextScale);
    setStagePos({
      x: localMid.x - worldX * nextScale + dx,
      y: localMid.y - worldY * nextScale + dy,
    });
  }, [setScale, setStagePos, stageRef]);

  const handleTouchEnd = React.useCallback((e) => {
    const touches = e.evt?.touches;
    if (!touches || touches.length < 2) {
      const g = gestureRef.current;
      const wasActive = g.active;
      g.active = false;
      // If the user was actively two-finger-panning, kick off inertia.
      // Don't kick off if velocity is tiny — avoids tiny drift on a clean release.
      if (wasActive && Math.hypot(g.vx, g.vy) > 0.6) {
        startMomentum();
      } else {
        g.vx = 0; g.vy = 0;
      }
    }
  }, [startMomentum]);

  // Desktop trackpad pinch (browsers emit wheel events with ctrlKey=true)
  // and Ctrl/Cmd + wheel zoom. Plain wheel falls through to default scroll.
  const handleWheel = React.useCallback((e) => {
    const evt = e.evt;
    if (!evt) return;
    const isZoomGesture = evt.ctrlKey || evt.metaKey;
    if (!isZoomGesture) return;
    evt.preventDefault();
    // Cancel any momentum drift — a zoom should anchor to the cursor cleanly.
    stopMomentum();

    const stageEl = stageRef.current?.container();
    const rect = stageEl ? stageEl.getBoundingClientRect() : { left: 0, top: 0 };
    const localX = evt.clientX - rect.left;
    const localY = evt.clientY - rect.top;

    // Smooth zoom factor. deltaY > 0 means user pinched in (zoom out).
    const zoomFactor = Math.exp(-evt.deltaY * 0.01);
    const nextScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale * zoomFactor));
    if (nextScale === scale) return;

    const worldX = (localX - stagePos.x) / scale;
    const worldY = (localY - stagePos.y) / scale;
    setScale(nextScale);
    setStagePos({
      x: localX - worldX * nextScale,
      y: localY - worldY * nextScale,
    });
  }, [scale, stagePos, setScale, setStagePos, stageRef, stopMomentum]);

  // ── Part A: snapping + alignment guides ──
  // One imperative guide layer, updated in onDragMove (no React state/mousemove).
  const guideLayerRef = React.useRef();
  const SNAP_PX = 6;

  const clearGuides = React.useCallback(() => {
    const gl = guideLayerRef.current;
    if (gl) { gl.destroyChildren(); gl.batchDraw(); }
  }, []);

  const handleLayerDragMove = React.useCallback((e) => {
    const node = e.target;
    const stage = node.getStage && node.getStage();
    if (!stage || node === stage) return;
    // Ignore the Transformer's own drag events (they fire alongside the node's
    // and would otherwise wipe the guides we just drew for the node).
    const ncn = node.getClassName && node.getClassName();
    if (ncn === 'Transformer') return;
    // Alt/Option temporarily disables snapping
    if (e.evt && e.evt.altKey) { clearGuides(); return; }
    const layer = node.getLayer();
    if (!layer) return;

    const threshold = SNAP_PX / scale; // in canvas units

    // Candidate lines from canvas edges + centre
    const vLines = [0, canvasWidth / 2, canvasWidth];
    const hLines = [0, canvasHeight / 2, canvasHeight];

    // …plus every other shape's edges + centres
    layer.getChildren().forEach((child) => {
      if (child === node) return;
      const cn = child.getClassName && child.getClassName();
      if (cn === 'Transformer') return;
      if (child.name && child.name() === 'canvas-bg') return;
      let box;
      try { box = child.getClientRect({ relativeTo: layer, skipShadow: true }); } catch (_) { return; }
      if (!box || box.width === 0 || box.height === 0) return;
      vLines.push(box.x, box.x + box.width / 2, box.x + box.width);
      hLines.push(box.y, box.y + box.height / 2, box.y + box.height);
    });

    let dragBox;
    try { dragBox = node.getClientRect({ relativeTo: layer, skipShadow: true }); } catch (_) { return; }
    const nodeV = [dragBox.x, dragBox.x + dragBox.width / 2, dragBox.x + dragBox.width];
    const nodeH = [dragBox.y, dragBox.y + dragBox.height / 2, dragBox.y + dragBox.height];

    const pick = (edges, lines) => {
      let best = { diff: threshold, delta: null, line: null };
      edges.forEach((edge) => {
        lines.forEach((line) => {
          const d = line - edge;
          if (Math.abs(d) < best.diff) best = { diff: Math.abs(d), delta: d, line };
        });
      });
      return best;
    };

    const bx = pick(nodeV, vLines);
    const by = pick(nodeH, hLines);

    if (bx.delta != null) node.x(node.x() + bx.delta);
    if (by.delta != null) node.y(node.y() + by.delta);

    // Draw the matched guide lines imperatively
    const gl = guideLayerRef.current;
    if (gl) {
      gl.destroyChildren();
      const sw = 1 / scale;
      if (bx.line != null) gl.add(new Konva.Line({ points: [bx.line, 0, bx.line, canvasHeight], stroke: '#FF00C7', strokeWidth: sw, dash: [6 / scale, 4 / scale], listening: false }));
      if (by.line != null) gl.add(new Konva.Line({ points: [0, by.line, canvasWidth, by.line], stroke: '#FF00C7', strokeWidth: sw, dash: [6 / scale, 4 / scale], listening: false }));
      gl.batchDraw();
    }
  }, [scale, canvasWidth, canvasHeight, clearGuides]);

  return (
    <Stage ref={stageRef}
      width={canvasWidth * scale} height={canvasHeight * scale}
      scaleX={scale} scaleY={scale}
      x={stagePos.x} y={stagePos.y}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onWheel={handleWheel}
      onClick={handleDeselect} onTap={handleDeselect}>
      <Layer onDragMove={handleLayerDragMove} onDragEnd={clearGuides}>
        <Rect x={0} y={0} width={canvasWidth} height={canvasHeight} fill={bgColor} name="canvas-bg" />

        {elements.map((el) => {
          if (el.visible === false) return null;
          const isSel = el.id === selectedId;

          if (el.type === 'text') {
            return <EditableText key={el.id} element={el} isSelected={isSel}
              onSelect={() => handleSelect(el.id)}
              onDoubleEdit={() => engine.setInlineEditingId(el.id)}
              onChange={updateElement} />;
          }
          if (el.type === 'rect') {
            return <DraggableShape key={el.id} element={el} isSelected={isSel}
              onSelect={() => el.editable && handleSelect(el.id)} onChange={updateElement} />;
          }
          if (el.type === 'circle') {
            return <DraggableShape key={el.id} element={el} isSelected={isSel}
              onSelect={() => el.editable && handleSelect(el.id)} onChange={updateElement} />;
          }
          if (el.type === 'line') {
            return <Line key={el.id} {...el} listening={false} />;
          }
          if (el.type === 'image') {
            return <URLImage key={el.id} image={el} isSelected={isSel}
              onSelect={() => handleSelect(el.id)} onChange={updateElement} />;
          }
          if (el.type === 'photoGrid') {
            return (
              <ResizablePhotoGrid key={el.id} element={el}
                isSelected={isSel} onSelect={handleSelect} onChange={updateElement}
                onSlotSelect={handleGridSlotSelect}
                selectedSlotIndex={selectedGridSlot?.gridId === el.id ? selectedGridSlot.slotIndex : null}
                dragOverSlotIndex={dragOverSlot?.gridId === el.id ? dragOverSlot.slotIndex : null}
                onPanChange={handleGridPanChange}
              />
            );
          }
          if (el.type === 'photoPlaceholder') {
            const isPSel = selectedPhotoPlaceholder === el.id;
            return (
              <Group key={el.id} x={el.x} y={el.y} draggable={el.draggable}
                onClick={() => handlePhotoPlaceholderSelect(el.id)}
                onTap={() => handlePhotoPlaceholderSelect(el.id)}
                onDragEnd={(e) => updateElement({ ...el, x: e.target.x(), y: e.target.y() })}>
                <Rect width={el.width} height={el.height} fill={el.backgroundColor || '#F0F0F0'}
                  stroke={isPSel ? '#0ea5e9' : (el.borderColor || '#CCCCCC')} strokeWidth={isPSel ? 3 : 2}
                  dash={[10, 5]} cornerRadius={4} />
                <Rect x={el.width / 2 - 30} y={el.height / 2 - 30} width={60} height={60}
                  fill="rgba(0,0,0,0.1)" cornerRadius={30} />
                <KonvaText x={el.width / 2 - 8} y={el.height / 2 - 12} text="+"
                  fontSize={32} fill="#888888" fontFamily="Arial" />
                <KonvaText x={0} y={el.height / 2 + 35} width={el.width}
                  text={el.label || 'CLICK TO ADD PHOTO'} fontSize={14} fill="#888888" fontFamily="Arial" align="center" />
              </Group>
            );
          }
          if (el.type === 'logoPlaceholder') {
            return (
              <Group key={el.id} x={el.x} y={el.y}>
                <Rect width={el.width} height={el.height} fill={el.backgroundColor || '#E5E5E5'}
                  stroke={el.borderColor || '#999999'} strokeWidth={2} dash={[10, 5]} cornerRadius={4} />
                <KonvaText x={0} y={el.height / 2 - 10} width={el.width}
                  text={brandKit?.logo_url ? 'LOGO (from Brand Kit)' : 'LOGO (Set up in Profile)'}
                  fontSize={12} fill="#666666" fontFamily="Arial" align="center" />
              </Group>
            );
          }
          if (el.type === 'headshotPlaceholder') {
            return (
              <Group key={el.id} x={el.x} y={el.y}>
                <Rect width={el.width} height={el.height} fill={el.backgroundColor || '#F5F5F5'}
                  stroke={el.borderColor || '#999999'} strokeWidth={2} dash={[10, 5]}
                  cornerRadius={el.cornerRadius != null ? el.cornerRadius : Math.min(el.width, el.height) / 2} />
                <KonvaText x={0} y={el.height / 2 - 10} width={el.width}
                  text={brandKit?.headshot_url ? 'HEADSHOT (from Brand Kit)' : 'HEADSHOT (Set up in Profile)'}
                  fontSize={12} fill="#666666" fontFamily="Arial" align="center" />
              </Group>
            );
          }
          if (el.type === 'qrCodePlaceholder') {
            return <QRCodeElement key={el.id} element={el} isSelected={isSel}
              onSelect={() => handleSelect(el.id)} onChange={updateElement} />;
          }
          return null;
        })}
      </Layer>
      <Layer ref={guideLayerRef} listening={false} />
    </Stage>
  );
};
